/**
 * OsintEntityLaunchButton — start an OSINT investigation for an entity that is
 * already in the case graph.
 *
 * Rendered from three surfaces (the graph detail sidebar, the identity detail,
 * and the entity inspector inside the document overlay), which between them
 * hold three different shapes of entity data. All three can name the case, the
 * node's element id and the node's type label, so that is the whole contract:
 * the type decides whether the button is offered at all, and the server reads
 * the node and composes the brief from its stored properties.
 *
 * Nothing navigates on success — the investigation runs in the background and a
 * toast points the user at the OSINT workspace.
 *
 * Lives under `features/` because it value-imports react-query and the API
 * client, which the frontend purity rule forbids inside `src/components/**`.
 * Presentational hosts take it as a `ReactNode` slot instead.
 */

import { Globe, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/sonner'
import { useCreateOsintInvestigationForEntity } from '@/features/osint/hooks/use-osint'
import { isOsintInvestigableType } from '@/features/osint/investigable-entities'
import { OsintEgressDialog, useOsintEgressGate } from '@/features/osint/OsintEgressNotice'
import { OSINT_EXTENSION_ID, useExtensionEnabled } from '@/hooks/use-extensions'

export interface OsintEntityLaunchTarget {
  caseId: string
  /** The node's Neo4j element id — what every surface holds. */
  entityId: string
  /** Only used for the toast copy; the brief's subject comes from the server. */
  entityName?: string | null
  /**
   * The entity's type label, as the host surface displays it. Required rather
   * than optional so a new surface has to state what it is launching on — an
   * unknown type hides the button (see `isOsintInvestigableType`).
   */
  entityType: string | null | undefined
}

interface Props {
  target: OsintEntityLaunchTarget
  /** Grouped so hosts can match their own action row without a wide prop list. */
  appearance?: {
    variant?: 'outline' | 'ghost'
    size?: 'sm' | 'xs'
    iconOnly?: boolean
    className?: string
  }
}

export function OsintEntityLaunchButton({ target, appearance = {} }: Props) {
  const { t } = useTranslation()
  const { enabled: osintEnabled } = useExtensionEnabled(OSINT_EXTENSION_ID)
  const create = useCreateOsintInvestigationForEntity(target.caseId)
  const egress = useOsintEgressGate(target.caseId)
  const { variant = 'outline', size = 'sm', iconOnly = false, className } = appearance

  const launch = () => {
    // Same one-time acknowledgement the OSINT composer uses — this is still the
    // moment the deployment starts talking to the outside world.
    egress.guard(async () => {
      try {
        await create.mutateAsync({ entityId: target.entityId })
        toast.success(
          target.entityName
            ? t('osint.entityLaunch.started', { name: target.entityName })
            : t('osint.entityLaunch.startedGeneric')
        )
      } catch (err) {
        // A deployment with the agent switched off answers 503; that is a
        // configuration answer, not a failure the user can retry away.
        const status = (err as { status?: number } | null)?.status
        toast.error(
          status === 503 ? t('osint.entityLaunch.disabled') : t('osint.entityLaunch.error')
        )
      }
    })
  }

  // Both gates remove the affordance everywhere at once — the three host
  // surfaces render this slot unconditionally. The extension toggle answers
  // "may this deployment run OSINT at all", the type answers "is there
  // anything to research about this entity".
  if (!osintEnabled) return null
  if (!isOsintInvestigableType(target.entityType)) return null

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={launch}
        disabled={create.isPending}
        className={className}
        title={t('osint.entityLaunch.action')}
      >
        {create.isPending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Globe className="size-4" />
        )}
        {!iconOnly && t('osint.entityLaunch.action')}
      </Button>
      <OsintEgressDialog open={egress.open} onConfirm={egress.confirm} onCancel={egress.cancel} />
    </>
  )
}
